<template>
  <div>
    <a-spin :spinning="loading">
      <div class="card">
        <div class="card-header">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0">
              <strong>API Keys Management</strong>
            </h5>
            <a-button type="primary" @click="showCreateModal" icon="plus">
              Create API Key
            </a-button>
          </div>
        </div>
        <div class="card-body">
          <!-- API Keys Table -->
          <a-table
            :columns="columns"
            :data-source="apiKeys"
            :pagination="{ pageSize: 10 }"
            :scroll="{ x: 1200 }"
            :row-key="(record) => record.id"
          >
            <!-- Status Column -->
            <template slot="status" slot-scope="text, record">
              <a-tag :color="getStatusColor(record)">
                {{ getStatusText(record) }}
              </a-tag>
            </template>

            <!-- Validity Period Column -->
            <template slot="validity" slot-scope="text, record">
              <div>
                <div>
                  <strong>From:</strong> {{ formatDate(record.valid_from) }}
                </div>
                <div>
                  <strong>To:</strong> {{ formatDate(record.valid_to) }}
                </div>
              </div>
            </template>

            <!-- Last Used Column -->
            <template slot="lastUsed" slot-scope="text, record">
              <span v-if="record.last_used_at">
                {{ formatDate(record.last_used_at) }}
              </span>
              <span v-else class="text-muted">Never used</span>
            </template>

            <!-- EPEX Member Column -->
            <template slot="epexMember" slot-scope="text, record">
              <span v-if="record.epex_member">
                {{ record.epex_member.epex_name }}
              </span>
              <span v-else class="text-muted">No member assigned</span>
            </template>

            <!-- Actions Column -->
            <template slot="actions" slot-scope="text, record">
              <a-dropdown>
                <a-menu slot="overlay">
                  <a-menu-item
                    v-if="record.is_active"
                    :key="`deactivate-${record.id}`"
                    @click="deactivateApiKey(record)"
                  >
                    <a-icon type="stop" /> Deactivate
                  </a-menu-item>
                  <a-menu-item
                    v-if="!record.is_active && !isExpired(record)"
                    :key="`reactivate-${record.id}`"
                    @click="reactivateApiKey(record)"
                  >
                    <a-icon type="play-circle" /> Reactivate
                  </a-menu-item>
                  <a-menu-item
                    v-if="record.is_active && !isExpired(record)"
                    :key="`generate-${record.id}`"
                    @click="generateNewToken(record)"
                  >
                    <a-icon type="key" /> Generate New Token
                  </a-menu-item>
                  <a-menu-item
                    :key="`edit-${record.id}`"
                    @click="editApiKey(record)"
                  >
                    <a-icon type="edit" /> Edit
                  </a-menu-item>
                  <a-menu-divider />
                  <a-menu-item
                    :key="`delete-${record.id}`"
                    @click="confirmDelete(record)"
                    class="text-danger"
                  >
                    <a-icon type="delete" /> Delete
                  </a-menu-item>
                </a-menu>
                <a-button size="small">
                  Actions <a-icon type="down" />
                </a-button>
              </a-dropdown>
            </template>
          </a-table>
        </div>
      </div>
    </a-spin>

    <!-- Create/Edit API Key Modal -->
    <a-modal
      :title="isEditing ? 'Edit API Key' : 'Create New API Key'"
      :visible="modalVisible"
      :confirm-loading="modalLoading"
      @ok="handleModalOk"
      @cancel="handleModalCancel"
      :width="600"
    >
      <a-form-model
        ref="apiKeyForm"
        :model="form"
        :rules="rules"
        :label-col="{ span: 6 }"
        :wrapper-col="{ span: 18 }"
      >
        <a-form-model-item label="Name" prop="name">
          <a-input
            v-model="form.name"
            placeholder="Enter a descriptive name for this API key"
          />
        </a-form-model-item>

        <a-form-model-item label="Contact Full Name" prop="contact_full_name">
          <a-input
            v-model="form.contact_full_name"
            placeholder="Enter contact person's full name (optional)"
          />
        </a-form-model-item>

        <a-form-model-item label="Contact Email" prop="contact_email">
          <a-input
            v-model="form.contact_email"
            placeholder="Enter contact person's email (optional)"
          />
        </a-form-model-item>

        <a-form-model-item label="EPEX Member" prop="epex_member_id">
          <a-select
            v-model="form.epex_member_id"
            placeholder="Select EPEX Member (optional)"
            allow-clear
            show-search
            :filter-option="filterOption"
          >
            <a-select-option
              v-for="member in epexMembers"
              :key="member.id"
              :value="member.id"
            >
              {{ member.epex_name }} ({{ member.eic_code }})
            </a-select-option>
          </a-select>
        </a-form-model-item>

        <a-form-model-item label="Valid From" prop="valid_from">
          <a-date-picker
            v-model="form.valid_from"
            show-time
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="Select start date and time"
            style="width: 100%;"
          />
        </a-form-model-item>

        <a-form-model-item label="Valid To" prop="valid_to">
          <a-date-picker
            v-model="form.valid_to"
            show-time
            format="YYYY-MM-DD HH:mm:ss"
            placeholder="Select end date and time"
            style="width: 100%;"
          />
        </a-form-model-item>

        <a-form-model-item label="Active" prop="is_active">
          <a-switch v-model="form.is_active" />
          <span class="ml-2 text-muted">
            {{ form.is_active ? 'API key is active' : 'API key is inactive' }}
          </span>
        </a-form-model-item>
      </a-form-model>
    </a-modal>

    <!-- Token Display Modal -->
    <a-modal
      title="API Key Token Generated"
      :visible="tokenModalVisible"
      @ok="tokenModalVisible = false"
      @cancel="tokenModalVisible = false"
      :footer="null"
      :width="700"
    >
      <a-alert
        message="Important: Save this token securely"
        description="This JWT token will only be shown once. Make sure to copy and store it securely as you won't be able to see it again."
        type="warning"
        show-icon
        class="mb-3"
      />

      <div class="token-display">
        <label class="font-weight-bold">JWT Token:</label>
        <a-textarea
          :value="generatedToken"
          :rows="6"
          readonly
          class="mt-2"
          style="font-family: monospace; font-size: 12px;"
        />
      </div>

      <template slot="footer">
        <a-button type="primary" @click="copyToken">
          Copy Token
        </a-button>
        <a-button @click="tokenModalVisible = false">
          Close
        </a-button>
      </template>
    </a-modal>
  </div>
</template>

<script>
import Vue from 'vue'
import {
  Button,
  Col,
  Row,
  Table,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Tag,
  Dropdown,
  Menu,
  Icon,
  Alert,
  message,
  Spin,
} from 'ant-design-vue'

Vue.use(
  Row,
  Col,
  Button,
  Table,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  Switch,
  Tag,
  Dropdown,
  Menu,
  Icon,
  Alert,
  Spin
)

import { ApiKeyService, EpexService } from '@/services/api'
import moment from 'moment'

export default {
  data() {
    return {
      loading: false,
      modalVisible: false,
      modalLoading: false,
      tokenModalVisible: false,
      isEditing: false,
      generatedToken: '',
      apiKeys: [],
      epexMembers: [],

      columns: [
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          width: 200,
          sorter: (a, b) => a.name.localeCompare(b.name),
        },
        {
          title: 'Contact Name',
          dataIndex: 'contact_full_name',
          key: 'contact_full_name',
          width: 200,
        },
        {
          title: 'Contact Email',
          dataIndex: 'contact_email',
          key: 'contact_email',
          width: 220,
        },
        {
          title: 'Status',
          key: 'status',
          width: 120,
          scopedSlots: { customRender: 'status' },
          filters: [
            { text: 'Active', value: 'active' },
            { text: 'Inactive', value: 'inactive' },
            { text: 'Expired', value: 'expired' },
          ],
          onFilter: (value, record) => {
            if (value === 'active')
              return record.is_active && !this.isExpired(record)
            if (value === 'inactive') return !record.is_active
            if (value === 'expired') return this.isExpired(record)
            return false
          },
        },
        {
          title: 'EPEX Member',
          key: 'epex_member',
          width: 180,
          scopedSlots: { customRender: 'epexMember' },
        },
        {
          title: 'Validity Period',
          key: 'validity',
          width: 200,
          scopedSlots: { customRender: 'validity' },
        },
        {
          title: 'Last Used',
          key: 'last_used_at',
          width: 150,
          scopedSlots: { customRender: 'lastUsed' },
          sorter: (a, b) => {
            if (!a.last_used_at && !b.last_used_at) return 0
            if (!a.last_used_at) return 1
            if (!b.last_used_at) return -1
            return moment(a.last_used_at).unix() - moment(b.last_used_at).unix()
          },
        },
        {
          title: 'Created',
          dataIndex: 'created_at',
          key: 'created_at',
          width: 150,
          customRender: (text) => this.formatDate(text),
          sorter: (a, b) =>
            moment(a.created_at).unix() - moment(b.created_at).unix(),
        },
        {
          title: 'Actions',
          key: 'actions',
          width: 120,
          fixed: 'right',
          scopedSlots: { customRender: 'actions' },
        },
      ],

      form: {
        name: '',
        contact_full_name: '',
        contact_email: '',
        epex_member_id: null,
        valid_from: null,
        valid_to: null,
        is_active: true,
      },

      rules: {
        name: [
          {
            required: true,
            message: 'Please enter a name for the API key',
            trigger: 'blur',
          },
          {
            min: 3,
            max: 255,
            message: 'Name must be between 3 and 255 characters',
            trigger: 'blur',
          },
        ],
        contact_full_name: [
          {
            max: 255,
            message: 'Full name must be at most 255 characters',
            trigger: 'blur',
          },
        ],
        contact_email: [
          {
            type: 'email',
            message: 'Please enter a valid email address',
            trigger: 'blur',
          },
          {
            max: 255,
            message: 'Email must be at most 255 characters',
            trigger: 'blur',
          },
        ],
        valid_from: [
          {
            required: true,
            message: 'Please select a start date',
            trigger: 'change',
          },
        ],
        valid_to: [
          {
            required: true,
            message: 'Please select an end date',
            trigger: 'change',
          },
          { validator: this.validateEndDate, trigger: 'change' },
        ],
      },
    }
  },

  methods: {
    async loadData() {
      this.loading = true
      try {
        const [apiKeysResponse, epexResponse] = await Promise.all([
          ApiKeyService.getAll(),
          EpexService.getMembers(),
        ])

        this.apiKeys = apiKeysResponse.data.data || []
        this.epexMembers = epexResponse.data.data || []
      } catch (error) {
        this.$notification.error({
          message: 'Error Loading Data',
          description: 'Failed to load API keys and EPEX members.',
        })
      } finally {
        this.loading = false
      }
    },

    showCreateModal() {
      this.isEditing = false
      this.resetForm()
      this.modalVisible = true
    },

    editApiKey(record) {
      this.isEditing = true
      const epexMemberId = record.epex_member_id != null
        ? record.epex_member_id
        : (record.epex_member && record.epex_member.id) || null
      this.form = {
        id: record.id,
        name: record.name,
        contact_full_name: record.contact_full_name || '',
        contact_email: record.contact_email || '',
        epex_member_id: epexMemberId,
        valid_from: moment(record.valid_from),
        valid_to: moment(record.valid_to),
        is_active: record.is_active,
      }
      this.modalVisible = true
    },

    resetForm() {
      this.form = {
        name: '',
        contact_full_name: '',
        contact_email: '',
        epex_member_id: null,
        valid_from: moment(),
        valid_to: moment().add(1, 'year'),
        is_active: true,
      }
      if (this.$refs.apiKeyForm) {
        this.$refs.apiKeyForm.resetFields()
      }
    },

    async handleModalOk() {
      try {
        await this.$refs.apiKeyForm.validate()
        this.modalLoading = true

        const formData = {
          name: this.form.name,
          contact_full_name: this.form.contact_full_name || null,
          contact_email: this.form.contact_email || null,
          epex_member_id: this.form.epex_member_id || null,
          valid_from: this.form.valid_from.toISOString(),
          valid_to: this.form.valid_to.toISOString(),
          is_active: this.form.is_active,
        }

        if (this.isEditing) {
          await ApiKeyService.update(this.form.id, formData)
          this.$notification.success({
            message: 'API Key Updated',
            description: 'The API key has been updated successfully.',
          })
        } else {
          const response = await ApiKeyService.create(formData)
          this.generatedToken = response.data.jwt_token
          this.tokenModalVisible = true
          this.$notification.success({
            message: 'API Key Created',
            description: 'The API key has been created successfully.',
          })
        }

        this.modalVisible = false
        this.loadData()
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: this.isEditing
            ? 'Failed to update API key.'
            : 'Failed to create API key.',
        })
      } finally {
        this.modalLoading = false
      }
    },

    handleModalCancel() {
      this.modalVisible = false
      this.resetForm()
    },

    async deactivateApiKey(record) {
      try {
        await ApiKeyService.deactivate(record.id)
        this.$notification.success({
          message: 'API Key Deactivated',
          description: `API key "${record.name}" has been deactivated.`,
        })
        Object.assign(record, { is_active: false })
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to deactivate API key.',
        })
      }
    },

    async reactivateApiKey(record) {
      try {
        await ApiKeyService.reactivate(record.id)
        this.$notification.success({
          message: 'API Key Reactivated',
          description: `API key "${record.name}" has been reactivated.`,
        })
        this.loadData()
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to reactivate API key.',
        })
      }
    },

    async generateNewToken(record) {
      try {
        const response = await ApiKeyService.generateToken(record.id)
        this.generatedToken = response.data.jwt_token
        this.tokenModalVisible = true
        this.$notification.success({
          message: 'New Token Generated',
          description: `A new JWT token has been generated for "${record.name}".`,
        })
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to generate new token.',
        })
      }
    },

    confirmDelete(record) {
      Modal.confirm({
        title: 'Delete API Key',
        content: `Are you sure you want to permanently delete the API key "${record.name}"? This action cannot be undone.`,
        okText: 'Delete',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: () => this.deleteApiKey(record),
      })
    },

    async deleteApiKey(record) {
      try {
        await ApiKeyService.delete(record.id)
        this.$notification.success({
          message: 'API Key Deleted',
          description: `API key "${record.name}" has been permanently deleted.`,
        })
        this.loadData()
      } catch (error) {
        this.$notification.error({
          message: 'Error',
          description: 'Failed to delete API key.',
        })
      }
    },

    copyToken() {
      navigator.clipboard
        .writeText(this.generatedToken)
        .then(() => {
          message.success('Token copied to clipboard!')
        })
        .catch(() => {
          message.error('Failed to copy token to clipboard')
        })
    },

    validateEndDate(rule, value, callback) {
      if (
        value &&
        this.form.valid_from &&
        value.isBefore(this.form.valid_from)
      ) {
        callback(new Error('End date must be after start date'))
      } else {
        callback()
      }
    },

    filterOption(input, option) {
      return (
        option.componentOptions.children[0].text
          .toLowerCase()
          .indexOf(input.toLowerCase()) >= 0
      )
    },

    formatDate(date) {
      return date ? moment(date).format('YYYY-MM-DD HH:mm') : ''
    },

    isExpired(record) {
      return moment().isAfter(moment(record.valid_to))
    },

    getStatusColor(record) {
      if (!record.is_active) return 'red'
      if (this.isExpired(record)) return 'orange'
      return 'green'
    },

    getStatusText(record) {
      if (!record.is_active) return 'Inactive'
      if (this.isExpired(record)) return 'Expired'
      return 'Active'
    },
  },

  mounted() {
    this.loadData()
  },
}
</script>

<style scoped>
.token-display {
  background: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  border: 1px solid #d9d9d9;
}

.text-muted {
  color: #999;
}

.text-danger {
  color: #ff4d4f;
}

.ml-2 {
  margin-left: 8px;
}

.mb-3 {
  margin-bottom: 16px;
}

.font-weight-bold {
  font-weight: 600;
}

.mt-2 {
  margin-top: 8px;
}
</style>
